import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { API_BASE_URL } from "../config";
import usePermissions from "../cms/hooks/usePermissions";

/* ── reusable inline styles ── */
const thStyle = { fontSize: "12px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", padding: "12px 16px" };
const tdStyle = { padding: "14px 16px", fontSize: "13px", color: "#6b7280" };
const statCard = { borderRadius: "12px", border: "none", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", transition: "transform 0.2s ease, box-shadow 0.2s ease" };
const actionBtn = (color, bg) => ({ width: "32px", height: "32px", padding: 0, borderRadius: "8px", backgroundColor: bg, border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" });
const gradientBadge = (bg) => ({ background: bg, color: "#fff", padding: "5px 14px", borderRadius: "6px", fontSize: "11px", fontWeight: 600, letterSpacing: "0.3px" });
const statusPill = (color, bg, border) => ({ display: "inline-flex", alignItems: "center", gap: "5px", padding: "4px 12px", borderRadius: "20px", fontSize: "11px", fontWeight: 600, backgroundColor: bg, color, border: `1px solid ${border}` });
const dotStyle = (color) => ({ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: color });
const inp = { borderRadius: "8px", border: "1.5px solid #e2e8f0", fontSize: "13px", padding: "9px 12px", width: "100%", outline: "none", boxSizing: "border-box", background: "white" };
const lbl = { fontSize: "12px", fontWeight: 700, color: "#374151", display: "block", marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.4px" };


/* ══════════════════════════════════════════════════════════════════════ */
/*  MAIN PAGE                                                             */
/* ══════════════════════════════════════════════════════════════════════ */
const TemplatesListPage = () => {
  const { canCreate, canEdit, canDelete } = usePermissions("/cms/templates");

  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("templates"); // "templates" | "placeholders"

  /* ── Template state ── */
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  /* Lead / customer split comes from the API (`context_type`), so the filter is a
     request param — with hundreds of templates a client-side filter would only
     ever search the page it had. */
  const [contextType, setContextType] = useState("all"); // "all" | "lead" | "customer"
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;

  // Typing shouldn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => { fetchTemplates(); }, [debouncedSearch, contextType]); // eslint-disable-line

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("cms_token");
      const res = await axios.get(`${API_BASE_URL}/get-email-template-list`, {
        params: {
          // "All" means no param at all — the API then returns both contexts.
          ...(contextType !== "all" ? { context_type: contextType } : {}),
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.status && Array.isArray(res.data.data)) setTemplates(res.data.data);
      else if (Array.isArray(res.data)) setTemplates(res.data);
      else setTemplates([]);
    } catch { toast.error("Failed to load templates"); }
    finally { setLoading(false); }
  };

  const handleDelete = (id, name) => {
    Swal.fire({
      title: "Delete Template?",
      html: `Are you sure you want to delete <strong>${name}</strong>?<br/>This action cannot be undone.`,
      icon: "warning", showCancelButton: true, confirmButtonColor: "#dc2626",
      confirmButtonText: "Yes, delete it",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("cms_token");
          await axios.delete(`${API_BASE_URL}/delete-email-template/${id}`, { headers: { Authorization: `Bearer ${token}` } });
          toast.success("Template deleted"); fetchTemplates();
        } catch { toast.error("Failed to delete template"); }
      }
    });
  };

  /* The request already asks the API to filter (context_type + search). This is
     the same match applied to whatever came back — the endpoint returns the whole
     list rather than a page, so the two can never disagree, and the box keeps
     working even if the API ignores the param. */
  const filtered = templates.filter((t) => {
    const q = debouncedSearch.trim().toLowerCase();
    const matchSearch =
      !q ||
      String(t.template_name || "").toLowerCase().includes(q) ||
      String(t.subject || "").toLowerCase().includes(q);
    const ctx = String(t.context_type || "").trim().toLowerCase();
    const matchCtx = contextType === "all" || ctx === contextType;
    return matchSearch && matchCtx;
  });

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const activeCount = templates.filter(t => t.status === "active").length;

  const TABS = [
    { key: "templates", label: "Email Templates", icon: "fas fa-envelope-open-text" },
    // { key: "placeholders", label: "Placeholder Manager", icon: "fas fa-tags" }, // feature paused
  ];

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          {/* Page Title */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0 font-size-18" style={{ fontWeight: 700, color: "#1f2937" }}>
                  <i className="fas fa-layer-group me-2" style={{ color: "#556ee6" }}></i>Template Manager
                </h4>
                <div className="page-title-right">
                  <ol className="breadcrumb m-0">
                    <li className="breadcrumb-item"><Link to="/cms/home">Dashboard</Link></li>
                    <li className="breadcrumb-item active">Templates</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {/* Stat Cards */}
          <div className="row mb-4">
            {[
              { label: "Total Templates", count: templates.length, bg: "linear-gradient(135deg,#667eea,#764ba2)", icon: "fas fa-layer-group" },
              { label: "Email Templates", count: templates.length, bg: "linear-gradient(135deg,#667eea,#764ba2)", icon: "fas fa-envelope-open-text" },
              { label: "Active Templates", count: activeCount, bg: "linear-gradient(135deg,#16a34a,#15803d)", icon: "fas fa-check-circle" },
            ].map((s, i) => (
              <div className="col-xl-3 col-md-6" key={i}>
                <div className="card mini-stats-wid" style={statCard}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.1)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.06)"; }}
                >
                  <div className="card-body d-flex align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-muted fw-medium mb-1" style={{ fontSize: "13px" }}>{s.label}</p>
                      <h4 className="mb-0" style={{ fontWeight: 700 }}>{s.count}</h4>
                    </div>
                    <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: s.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <i className={`${s.icon} text-white`} style={{ fontSize: "18px" }}></i>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Main Card with Tabs */}
          <div className="row">
            <div className="col-12">
              <div className="card" style={{ borderRadius: "12px", border: "none", boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }}>

                {/* Tab Header */}
                <div style={{ borderBottom: "2px solid #f0f4ff", padding: "0 24px", background: "#fafbff", borderRadius: "12px 12px 0 0" }}>
                  <div style={{ display: "flex", gap: "4px" }}>
                    {TABS.map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        style={{
                          padding: "14px 20px", border: "none", background: "none", cursor: "pointer", fontSize: "13px", fontWeight: 600,
                          color: activeTab === tab.key ? "#556ee6" : "#6b7280",
                          borderBottom: activeTab === tab.key ? "2px solid #556ee6" : "2px solid transparent",
                          marginBottom: "-2px", display: "flex", alignItems: "center", gap: "7px",
                          transition: "all 0.15s",
                        }}
                      >
                        <i className={tab.icon} style={{ fontSize: "13px" }}></i>
                        {tab.label}
                        {/* Both sides of the CRM are managed here, so say so. */}
                        {tab.key === "templates" && (
                          <span style={{
                            background: "#eef2ff", color: "#4338ca", border: "1px solid #c7d2fe",
                            borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 600,
                          }}>
                            Lead + Customer
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="card-body" style={{ padding: "24px" }}>

                  {/* ── TEMPLATES TAB ── */}
                  {activeTab === "templates" && (
                    <>
                      {/* Toolbar */}
                      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                        <div className="d-flex align-items-center gap-3">
                          <div className="position-relative">
                            <i className="fas fa-search position-absolute" style={{ top: "50%", left: "12px", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "13px" }}></i>
                            <input type="text" className="form-control" placeholder="Search templates..."
                              value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                              style={{ paddingLeft: "36px", borderRadius: "8px", border: "1px solid #e5e7eb", fontSize: "14px", minWidth: "260px" }}
                            />
                          </div>
                          <div className="btn-group" role="group">
                            {[
                              { key: "all", label: "All" },
                              { key: "lead", label: "Lead" },
                              { key: "customer", label: "Customer" },
                            ].map((opt, i, arr) => (
                              <button key={opt.key} className={`btn btn-sm ${contextType === opt.key ? "btn-primary" : "btn-outline-secondary"}`}
                                onClick={() => { setContextType(opt.key); setCurrentPage(1); }}
                                style={{
                                  borderRadius: i === 0 ? "8px 0 0 8px" : i === arr.length - 1 ? "0 8px 8px 0" : 0,
                                  fontSize: "13px",
                                  ...(contextType === opt.key ? { boxShadow: "0 2px 6px rgba(85,110,230,0.35)" } : {}),
                                }}
                              >{opt.label}</button>
                            ))}
                          </div>
                        </div>
                        {canCreate && (
                        <button className="btn btn-sm d-flex align-items-center gap-2" onClick={() => navigate("/cms/templates/email/create")}
                          style={{ background: "linear-gradient(135deg,#667eea,#764ba2)", color: "#fff", borderRadius: "8px", padding: "8px 18px", fontSize: "13px", fontWeight: 600, border: "none", boxShadow: "0 3px 10px rgba(102,126,234,0.3)" }}>
                          <i className="fas fa-plus" style={{ fontSize: "11px" }}></i> Email Template
                        </button>
                        )}
                      </div>

                      {/* Table */}
                      {loading ? (
                        <div className="text-center py-5">
                          <div className="spinner-border text-primary" role="status"></div>
                          <p className="text-muted mt-3 mb-0" style={{ fontSize: "14px" }}>Loading templates...</p>
                        </div>
                      ) : paginated.length === 0 ? (
                        <div className="text-center py-5">
                          <i className="fas fa-inbox text-muted mb-3" style={{ fontSize: "48px", opacity: 0.3 }}></i>
                          <p className="text-muted mb-1" style={{ fontSize: "16px", fontWeight: 600 }}>No templates found</p>
                          <p className="text-muted mb-0" style={{ fontSize: "13px" }}>Create your first template to get started</p>
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0">
                            <thead>
                              <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                                {["#", "Template Name", "Type", "Subject / Preview", "Status", "Created", "Actions"].map((h, i) => (
                                  <th key={i} style={{ ...thStyle, ...(h === "Actions" ? { textAlign: "center" } : {}), ...(h === "#" ? { width: "50px" } : {}) }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {paginated.map((t, idx) => (
                                <tr key={t.id || idx} style={{ borderBottom: "1px solid #f3f4f6", transition: "background 0.15s" }}
                                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#f8f9fe")}
                                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = "")}
                                >
                                  <td style={tdStyle}>{(currentPage - 1) * perPage + idx + 1}</td>
                                  <td style={{ padding: "14px 16px" }}>
                                    <span style={{ fontSize: "14px", fontWeight: 600, color: "#1f2937" }}>{t.template_name || "Untitled"}</span>
                                  </td>
                                  <td style={{ padding: "14px 16px" }}>
                                    <div className="d-flex align-items-center gap-1 flex-wrap">
                                      <span className="badge d-inline-flex align-items-center gap-1" style={gradientBadge("linear-gradient(135deg,#667eea,#764ba2)")}>
                                        <i className="fas fa-envelope-open-text" style={{ fontSize: "10px" }}></i> Email
                                      </span>
                                      {/* Which side of the CRM the template belongs to */}
                                      {(() => {
                                        const ctx = String(t.context_type || "").trim().toLowerCase();
                                        if (ctx !== "lead" && ctx !== "customer") return null;
                                        const isLead = ctx === "lead";
                                        return (
                                          <span className="badge" style={{
                                            background: isLead ? "#e0f2fe" : "#f0fdf4",
                                            color: isLead ? "#0369a1" : "#15803d",
                                            border: `1px solid ${isLead ? "#bae6fd" : "#bbf7d0"}`,
                                            borderRadius: 20, padding: "3px 9px", fontSize: 11, fontWeight: 600,
                                          }}>
                                            {isLead ? "Lead" : "Customer"}
                                          </span>
                                        );
                                      })()}
                                    </div>
                                  </td>
                                  <td style={{ padding: "14px 16px", maxWidth: "280px" }}>
                                    <span className="text-truncate d-inline-block" style={{ maxWidth: "260px", fontSize: "13px", color: "#6b7280" }}>{t.subject || "—"}</span>
                                  </td>
                                  <td style={{ padding: "14px 16px" }}>
                                    {t.status === "active"
                                      ? <span style={statusPill("#22c55e", "#f0fff4", "#bbf7d0")}><span style={dotStyle("#22c55e")}></span>Active</span>
                                      : <span style={statusPill("#ef4444", "#fff5f5", "#fecaca")}><span style={dotStyle("#ef4444")}></span>Inactive</span>
                                    }
                                  </td>
                                  <td style={{ ...tdStyle, fontSize: "12.5px" }}>
                                    {t.created_at ? new Date(t.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                                  </td>
                                  <td style={{ padding: "14px 16px", textAlign: "center" }}>
                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                      {[
                                        ...(canEdit ? [{ icon: "fas fa-pen", color: "#f59e0b", bg: "#fffbeb", title: "Edit", onClick: () => navigate(`/cms/templates/email/${t.id}/edit`) }] : []),
                                        ...(canDelete ? [{ icon: "fas fa-trash-alt", color: "#dc2626", bg: "#fef2f2", title: "Delete", onClick: () => handleDelete(t.id, t.template_name) }] : []),
                                      ].map((a, ai) => (
                                        <button key={ai} className="btn btn-sm" title={a.title} onClick={a.onClick} style={actionBtn(a.color, a.bg)}
                                          onMouseEnter={e => { e.currentTarget.style.backgroundColor = a.color; e.currentTarget.querySelector("i").style.color = "#fff"; }}
                                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = a.bg; e.currentTarget.querySelector("i").style.color = a.color; }}
                                        >
                                          <i className={a.icon} style={{ fontSize: "12px", color: a.color, transition: "color 0.2s" }}></i>
                                        </button>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="d-flex align-items-center justify-content-between mt-4 pt-3" style={{ borderTop: "1px solid #f3f4f6" }}>
                          <span style={{ fontSize: "13px", color: "#6b7280" }}>Showing {(currentPage - 1) * perPage + 1}–{Math.min(currentPage * perPage, filtered.length)} of {filtered.length}</span>
                          <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                              <button className="page-link" onClick={() => setCurrentPage(p => p - 1)} style={{ borderRadius: "6px 0 0 6px" }}>
                                <i className="fas fa-chevron-left" style={{ fontSize: "11px" }}></i>
                              </button>
                            </li>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pg => (
                              <li key={pg} className={`page-item ${currentPage === pg ? "active" : ""}`}>
                                <button className="page-link" onClick={() => setCurrentPage(pg)}>{pg}</button>
                              </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                              <button className="page-link" onClick={() => setCurrentPage(p => p + 1)} style={{ borderRadius: "0 6px 6px 0" }}>
                                <i className="fas fa-chevron-right" style={{ fontSize: "11px" }}></i>
                              </button>
                            </li>
                          </ul>
                        </div>
                      )}
                    </>
                  )}

                  {/* Placeholder Manager tab — feature paused */}

                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default TemplatesListPage;
